import router, { resetRouter } from "@/router";
import index from "@/views/Layout/index";
import template from "@/views/Layout/template.vue";
import { getRouters } from "@/api/system";
import { hiddenRoutes } from "@/router/hiddenRoutes";
import { asyncRoutes, systemRoutes } from "@/router/asyncRoutes";
import {
  teacherStaticRoutes,
  TEACHER_DEFAULT_ROUTE
} from "@/router/teacherStaticRoutes";
import { TEACHER_STATIC_ROUTERS } from "@/config/teacherStaticRouters";
import {
  mapToAppPath,
  normalizePath,
  resolveFullPath
} from "@/router/routeMap";
import settings from "@/settings";

const viewModules = require.context("@/views", true, /\.vue$/);

let dynamicRoutesLoaded = false;
let defaultRoutePath = TEACHER_DEFAULT_ROUTE || "/home";
/** getRouters 原始 data，供路由与菜单共用，避免重复请求 */
let rawRoutersCache = null;
/** 进行中的 getRouters Promise（并发去重） */
let routersRequestPromise = null;

function isTeacherApp() {
  return !!(settings && settings.isTeacher);
}

/**
 * 拉取 getRouters 数据（带内存缓存 + 进行中请求去重）
 * @param {boolean} force 是否强制重新请求
 */
export async function fetchRoutersData(force = false) {
  // 教师端：写死 getRouters 结构，不请求接口
  if (isTeacherApp()) {
    rawRoutersCache = TEACHER_STATIC_ROUTERS;
    return TEACHER_STATIC_ROUTERS;
  }
  if (force) {
    rawRoutersCache = null;
    routersRequestPromise = null;
  }
  if (Array.isArray(rawRoutersCache)) {
    return rawRoutersCache;
  }
  if (!routersRequestPromise) {
    routersRequestPromise = getRouters()
      .then(result => {
        rawRoutersCache =
          result && Array.isArray(result.data) ? result.data : [];
        return rawRoutersCache;
      })
      .catch(err => {
        routersRequestPromise = null;
        throw err;
      });
  }
  return routersRequestPromise;
}

export function getRawRoutersCache() {
  return Array.isArray(rawRoutersCache) ? rawRoutersCache : null;
}

export function clearRoutersCache() {
  rawRoutersCache = null;
  routersRequestPromise = null;
}

function loadView(component) {
  // 后端 Layout 仅作嵌套占位，对应 ParentView；外层壳由 buildLayoutRoutes 统一挂载
  if (!component || component === "Layout") {
    return template;
  }
  if (component === "ParentView" || component === "template") {
    return template;
  }

  let view = String(component)
    .replace(/^views\//, "")
    .replace(/^\//, "")
    .replace(/\.vue$/, "");

  const candidates = [`./${view}.vue`, `./${view}/index.vue`];
  let matched = viewModules.keys().find(key => candidates.includes(key));
  if (!matched) {
    const lowerView = view.toLowerCase();
    matched = viewModules.keys().find(key => {
      const normalized = key
        .replace(/^\.\//, "")
        .replace(/\.vue$/, "")
        .toLowerCase();
      return normalized === lowerView || normalized === `${lowerView}/index`;
    });
  }
  if (matched) {
    return viewModules(matched).default || viewModules(matched);
  }

  console.warn("[dynamicRoutes] component not found:", component);
  return template;
}

function shouldSkipRoute(route) {
  if (!route) return true;
  if (route.menuType === "F") return true;
  return false;
}

function getModulePath(path) {
  const normalized = normalizePath(path);
  const segments = normalized.split("/").filter(Boolean);
  if (!segments.length) return normalized;
  return `/${segments[0]}`;
}

function transformRouteNode(route, parentPath = "") {
  if (shouldSkipRoute(route)) {
    return null;
  }

  const currentPath = resolveFullPath(parentPath, route.path || "");
  const item = {
    ...route,
    meta: {
      ...(route.meta || {}),
      title:
        (route.meta && route.meta.title) || route.menuName || route.name || "",
      requireAuth: true
    }
  };

  if (!parentPath) {
    item.path = getModulePath(currentPath);
  } else {
    const parent = normalizePath(parentPath);
    if (currentPath.startsWith(parent + "/")) {
      item.path = currentPath.slice(parent.length + 1);
    } else if (currentPath === parent) {
      item.path = normalizePath(route.path || "");
    } else {
      item.path = normalizePath(route.path || "");
    }
  }

  if (item.component) {
    item.component = loadView(item.component);
  }

  const parentForChildren = resolveFullPath(
    parentPath,
    item.path || route.path || ""
  );
  if (item.children && item.children.length) {
    item.children = item.children
      .map(child => transformRouteNode(child, parentForChildren))
      .filter(Boolean);

    if (!item.component && item.children.length) {
      item.component = template;
    }
  }

  if (!item.component && (!item.children || !item.children.length)) {
    return null;
  }

  return item;
}

function flattenRouterList(routers) {
  const result = [];

  (routers || []).forEach(item => {
    if (!item || item.hidden) return;

    const path = normalizePath(item.path);
    if (path === "/" && item.children && item.children.length) {
      item.children.forEach(child => {
        if (!child.hidden) {
          result.push({
            ...child,
            path: normalizePath(child.path),
            children: child.children || []
          });
        }
      });
      return;
    }

    result.push({
      ...item,
      path,
      children: item.children || []
    });
  });

  return result;
}

function transformApiRoutes(routerData) {
  return flattenRouterList(routerData)
    .map(item => transformRouteNode(item))
    .filter(Boolean);
}

function toLayoutChildRoute(route) {
  const absolutePath = normalizePath(route.path);
  if (!absolutePath) return null;

  // Layout(path: '/') 的 children 须用相对 path，否则 /home 等可能匹配不到
  // 进而触发守卫 next('/home') → Redirected when going from "/login" to "/home"
  const path = absolutePath.replace(/^\//, "");

  if (route.children && route.children.length) {
    return {
      ...route,
      path
    };
  }

  return {
    ...route,
    path
  };
}

function splitSystemRoute(routes) {
  const systemIndex = routes.findIndex(
    item => normalizePath(item.path) === "/system"
  );
  if (systemIndex === -1) {
    return { mainRoutes: routes, systemRoute: null };
  }

  const systemRoute = routes[systemIndex];
  const mainRoutes = routes.filter((_, index) => index !== systemIndex);
  return { mainRoutes, systemRoute };
}

function ensureParentView(route) {
  const item = { ...route };
  if (item.component === index) {
    item.component = template;
  }
  if (item.children && item.children.length) {
    item.children = item.children.map(ensureParentView);
  }
  return item;
}

function buildLayoutRoutes(businessRoutes) {
  const normalized = businessRoutes
    .map(toLayoutChildRoute)
    .filter(Boolean)
    .map(ensureParentView);
  const { mainRoutes, systemRoute } = splitSystemRoute(normalized);
  const layoutRoutes = [];

  // hiddenRoutes 同样转为相对 path，挂到 Layout 下
  const layoutHiddenRoutes = (hiddenRoutes || []).map(route => {
    const absolutePath = normalizePath(route.path);
    if (!absolutePath) return null;
    return {
      ...route,
      path: absolutePath.replace(/^\//, "")
    };
  }).filter(Boolean);

  if (mainRoutes.length || layoutHiddenRoutes.length) {
    layoutRoutes.push({
      path: "/",
      component: index,
      redirect: TEACHER_DEFAULT_ROUTE || findDefaultPath(businessRoutes) || "/home",
      children: [...mainRoutes, ...layoutHiddenRoutes]
    });
  }

  if (systemRoute) {
    const normalizedSystem = ensureParentView(systemRoute);
    layoutRoutes.push({
      path: "/system",
      component: index,
      meta: normalizedSystem.meta,
      children: (normalizedSystem.children || []).map(child => {
        const p = normalizePath(child.path || "");
        return {
          ...child,
          path: p.replace(/^\//, "")
        };
      })
    });
  }

  return layoutRoutes;
}

function findDefaultPath(routes) {
  for (let i = 0; i < routes.length; i++) {
    const route = routes[i];
    if (route.children && route.children.length) {
      const childPath = resolveFullPath(route.path, route.children[0].path);
      if (childPath) return childPath;
    }
    if (route.path) return normalizePath(route.path);
  }
  return "";
}

function buildFallbackRoutes() {
  return buildLayoutRoutes([...asyncRoutes, systemRoutes]);
}

export function isDynamicRoutesLoaded() {
  return dynamicRoutesLoaded;
}

export function getDefaultRoutePath() {
  return TEACHER_DEFAULT_ROUTE || defaultRoutePath || "/home";
}

export async function loadDynamicRoutes() {
  if (dynamicRoutesLoaded) {
    return true;
  }

  // 教师端：直接使用写死的 Vue Router 路由（路径见 teacherStaticRoutes.js）
  const businessRoutes = [...teacherStaticRoutes];
  defaultRoutePath = TEACHER_DEFAULT_ROUTE || "/home";

  const layoutRoutes = buildLayoutRoutes(businessRoutes);
  if (!layoutRoutes.length) {
    return false;
  }

  const rootLayout = layoutRoutes.find(route => route.path === "/");
  defaultRoutePath = TEACHER_DEFAULT_ROUTE || "/home";
  if (rootLayout) {
    rootLayout.redirect = defaultRoutePath;
  }

  layoutRoutes.push({
    path: "*",
    redirect: defaultRoutePath,
    hidden: true
  });

  router.addRoutes(layoutRoutes);
  dynamicRoutesLoaded = true;
  return true;
}

export function resetDynamicRoutes() {
  dynamicRoutesLoaded = false;
  defaultRoutePath = TEACHER_DEFAULT_ROUTE || "/home";
  clearRoutersCache();
  resetRouter();
}

export { mapToAppPath, normalizePath, resolveFullPath };



// WEBPACK FOOTER //
// ./src/utils/dynamicRoutes.js